import { useCallback, useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import { PageContainer, PageHeader, EmptyState } from "@/components/app/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Users as UsersIcon, Plus, Key, CaretUp, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function UsersPage() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState("direct");
    const [form, setForm] = useState({ email: "", name: "", role: "org_user", password: "" });
    const [inviteResult, setInviteResult] = useState(null);
    const [pwUser, setPwUser] = useState(null);
    const [pwValue, setPwValue] = useState("");

    // Filtering by app + sorting
    const [forms, setForms] = useState([]);
    const [appFilter, setAppFilter] = useState("__all__");
    const [sortBy, setSortBy] = useState("name");
    const [sortDir, setSortDir] = useState(1);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [{ data: u }, fr] = await Promise.all([
                api.get("/users", { params: search ? { search } : {} }),
                api.get("/forms").catch(() => ({ data: [] })),
            ]);
            setUsers(u); setForms(fr.data);
        } catch (e) { toast.error(extractError(e)); }
        finally { setLoading(false); }
    }, [search]);

    useEffect(() => { load(); }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        const normalizedEmail = (form.email || "").trim().toLowerCase();
        try {
            if (mode === "direct") {
                if (!form.password || form.password.length < 6) return toast.error("Password must be 6+ characters");
                await api.post("/users", { ...form, email: normalizedEmail });
                toast.success("User created and activated");
                setOpen(false);
                setForm({ email: "", name: "", role: "org_user", password: "" });
                await load();
            } else {
                const { name, role } = form;
                const { data } = await api.post("/users/invite", { email: normalizedEmail, name, role });
                setInviteResult(data);
                toast.success("Invitation created");
                await load();
            }
        } catch (err) { toast.error(extractError(err)); }
    };

    const resetPassword = async () => {
        if (!pwValue || pwValue.length < 6) return toast.error("Password must be 6+ characters");
        try {
            await api.post(`/users/${pwUser.id}/set-password`, { password: pwValue });
            toast.success("Password updated; user activated");
            setPwUser(null); setPwValue("");
            await load();
        } catch (e) { toast.error(extractError(e)); }
    };

    const activate = async (id) => {
        try { await api.post(`/users/${id}/activate`); toast.success("User activated"); await load(); }
        catch (e) { toast.error(extractError(e)); }
    };

    const deactivate = async (id) => {
        if (!confirm("Deactivate this user?")) return;
        try { await api.delete(`/users/${id}`); toast.success("User deactivated"); await load(); }
        catch (e) { toast.error(extractError(e)); }
    };

    const filteredUsers = useMemo(() => {
        let arr = users;
        if (appFilter !== "__all__") {
            const f = forms.find((x) => x.id === appFilter);
            if (f) {
                const allow = new Set(f.access_user_ids || []);
                const allowGroups = new Set(f.access_group_ids || []);
                const allowRoles = new Set(f.access_role_ids || []);
                arr = arr.filter((u) => {
                    if (u.role === "platform_admin" || u.role === "org_admin") return true;
                    if (allow.has(u.id)) return true;
                    if ((u.group_ids || []).some((g) => allowGroups.has(g))) return true;
                    if ((u.role_ids || []).some((r) => allowRoles.has(r))) return true;
                    return false;
                });
            }
        }
        return [...arr].sort((a, b) => {
            const av = String(a[sortBy] ?? "").toLowerCase();
            const bv = String(b[sortBy] ?? "").toLowerCase();
            return av.localeCompare(bv) * sortDir;
        });
    }, [users, forms, appFilter, sortBy, sortDir]);

    const toggleSort = (col) => {
        if (sortBy === col) setSortDir(sortDir === 1 ? -1 : 1);
        else { setSortBy(col); setSortDir(1); }
    };
    const SortIcon = ({ col }) => sortBy === col ? (sortDir === 1 ? <CaretUp size={10} className="inline ml-1" /> : <CaretDown size={10} className="inline ml-1" />) : null;

    return (
        <PageContainer>
            <PageHeader
                overline="Access"
                title="Users"
                description="Invite team members and manage roles within your organization."
                actions={
                    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setInviteResult(null); setMode("direct"); } }}>
                        <DialogTrigger asChild>
                            <Button className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="add-user-btn">
                                <Plus size={16} className="mr-2" /> Add user
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-sm" data-testid="add-user-dialog">
                            <DialogHeader><DialogTitle>Add user</DialogTitle></DialogHeader>
                            {inviteResult ? (
                                <div className="space-y-3">
                                    <p className="text-sm">Invitation created. {inviteResult.email_status === "mocked" && "(Email service not configured — share this link manually)"}</p>
                                    <div className="border border-border bg-secondary p-3 rounded-sm font-mono text-xs break-all" data-testid="invite-link-display">
                                        {inviteResult.invite_link}
                                    </div>
                                    <DialogFooter><Button onClick={() => { setOpen(false); setInviteResult(null); }} className="rounded-sm">Done</Button></DialogFooter>
                                </div>
                            ) : (
                                <form onSubmit={submit} className="space-y-4">
                                    <div className="grid grid-cols-2 gap-px bg-border border border-border rounded-sm overflow-hidden">
                                        <button type="button" onClick={() => setMode("direct")} className={`p-3 text-sm transition-colors ${mode === "direct" ? "bg-foreground text-background" : "bg-card hover:bg-secondary"}`} data-testid="mode-direct-btn">
                                            <div className="font-medium">Create directly</div>
                                            <div className="text-[10px] opacity-70 mt-0.5 uppercase tracking-wider">Set password now</div>
                                        </button>
                                        <button type="button" onClick={() => setMode("invite")} className={`p-3 text-sm transition-colors ${mode === "invite" ? "bg-foreground text-background" : "bg-card hover:bg-secondary"}`} data-testid="mode-invite-btn">
                                            <div className="font-medium">Send invite</div>
                                            <div className="text-[10px] opacity-70 mt-0.5 uppercase tracking-wider">Email link</div>
                                        </button>
                                    </div>
                                    <div>
                                        <Label className="label-overline">Email</Label>
                                        <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 rounded-sm" data-testid="user-email-input" />
                                    </div>
                                    <div>
                                        <Label className="label-overline">Name{mode === "direct" ? "" : " (optional)"}</Label>
                                        <Input required={mode === "direct"} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-sm" data-testid="user-name-input" />
                                    </div>
                                    {mode === "direct" && (
                                        <div>
                                            <Label className="label-overline">Temporary password</Label>
                                            <Input type="password" minLength={6} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-2 rounded-sm font-mono" placeholder="6+ characters" data-testid="user-password-input" />
                                            <p className="text-xs text-muted-foreground mt-1">Share these credentials with the user. They can change it later via Forgot Password.</p>
                                        </div>
                                    )}
                                    <div>
                                        <Label className="label-overline">Role</Label>
                                        <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                            <SelectTrigger className="mt-2 rounded-sm" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="org_user">Org User</SelectItem>
                                                <SelectItem value="org_admin">Org Admin</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <DialogFooter><Button type="submit" className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="submit-user-btn">{mode === "direct" ? "Create & activate" : "Send invitation"}</Button></DialogFooter>
                                </form>
                            )}
                        </DialogContent>
                    </Dialog>
                }
                testid="users-header"
            />

            <div className="flex flex-wrap gap-2 mb-6 items-center" data-testid="users-filter-bar">
                <Input placeholder="Search by name or email" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="rounded-sm max-w-sm" data-testid="users-search-input" />
                <Button variant="outline" className="rounded-sm" onClick={load} data-testid="users-search-btn">Search</Button>
                <Select value={appFilter} onValueChange={setAppFilter}>
                    <SelectTrigger className="w-56 rounded-sm" data-testid="users-app-filter"><SelectValue placeholder="Filter by app" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all__">All apps</SelectItem>
                        {forms.map((f) => <SelectItem key={f.id} value={f.id}>{f.form_name}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground">Loading…</div>
            ) : users.length === 0 ? (
                <EmptyState icon={UsersIcon} title="No users yet" description="Invite your first team member." testid="users-empty" />
            ) : (
                <div className="brut-section overflow-hidden">
                    <table className="w-full" data-testid="users-table">
                        <thead><tr>
                            <th className="data-header text-left cursor-pointer select-none" onClick={() => toggleSort("name")} data-testid="users-sort-name">Name<SortIcon col="name" /></th>
                            <th className="data-header text-left cursor-pointer select-none" onClick={() => toggleSort("email")} data-testid="users-sort-email">Email<SortIcon col="email" /></th>
                            <th className="data-header text-left cursor-pointer select-none" onClick={() => toggleSort("role")} data-testid="users-sort-role">Role<SortIcon col="role" /></th>
                            <th className="data-header text-left">Status</th>
                            <th className="data-header"></th>
                        </tr></thead>
                        <tbody>
                            {filteredUsers.map((u) => (
                                <tr key={u.id} className="hover:bg-secondary/50" data-testid={`user-row-${u.id}`}>
                                    <td className="data-cell font-medium">{u.name}</td>
                                    <td className="data-cell text-muted-foreground">{u.email}</td>
                                    <td className="data-cell"><Badge variant="outline" className="rounded-sm font-mono text-xs">{u.role}</Badge></td>
                                    <td className="data-cell">{u.is_active ? <span className="text-xs uppercase tracking-wider text-primary">Active</span> : <span className="text-xs uppercase tracking-wider text-muted-foreground">Inactive</span>}</td>
                                    <td className="data-cell text-right">
                                        <div className="flex justify-end gap-1">
                                            <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => { setPwUser(u); setPwValue(""); }} data-testid={`setpw-${u.id}`}><Key size={14} className="mr-1" /> Set password</Button>
                                            {u.is_active ? (
                                                <Button variant="ghost" size="sm" className="rounded-sm text-destructive hover:text-destructive" onClick={() => deactivate(u.id)} data-testid={`deactivate-${u.id}`}>Deactivate</Button>
                                            ) : (
                                                <Button variant="ghost" size="sm" className="rounded-sm text-primary" onClick={() => activate(u.id)} data-testid={`activate-${u.id}`}>Activate</Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Password reset dialog */}
            <Dialog open={!!pwUser} onOpenChange={(v) => { if (!v) { setPwUser(null); setPwValue(""); } }}>
                <DialogContent className="rounded-sm" data-testid="setpw-dialog">
                    <DialogHeader><DialogTitle>Set password for {pwUser?.name}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div className="text-sm border border-border bg-secondary p-3 rounded-sm">{pwUser?.email}</div>
                        <div>
                            <Label className="label-overline">New password</Label>
                            <Input type="password" minLength={6} value={pwValue} onChange={(e) => setPwValue(e.target.value)} className="mt-2 rounded-sm font-mono" placeholder="6+ characters" data-testid="setpw-input" />
                            <p className="text-xs text-muted-foreground mt-1">User will be activated and able to log in immediately. Share the password securely.</p>
                        </div>
                        <DialogFooter><Button onClick={resetPassword} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="setpw-submit-btn">Update password</Button></DialogFooter>
                    </div>
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
}
